import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { api } from '../lib/api';
import { getVisitorId } from '../lib/tracker';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useCurrency } from '../context/CurrencyContext';
import SEO from '../components/SEO';

export default function Cart() {
    const { items, updateQty, removeItem, subtotal, clear, lineKey } = useCart();
    const { customer } = useCustomerAuth();
    const { format } = useCurrency();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        address: '',
        city: '',
        country: '',
        postal_code: '',
        notes: '',
    });

    useEffect(() => {
        if (customer) {
            setForm((f) => ({
                ...f,
                customer_email: f.customer_email || customer.email || '',
                customer_name: f.customer_name || customer.name || '',
            }));
        }
    }, [customer]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return;
        for (const field of ['customer_name', 'customer_email', 'customer_phone', 'address', 'city', 'country']) {
            if (!form[field]) { toast.error('Please complete all required fields.'); return; }
        }
        setSubmitting(true);
        try {
            const res = await api.post('/purchase-requests', { ...form, items, subtotal, visitor_id: getVisitorId() });
            toast.success('Request submitted. Our atelier will contact you shortly.');
            clear();
            navigate(`/confirmation/${res.data.reference}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not submit your request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="cart-page">
            <SEO title="Your Bag" canonical="/cart" noIndex />
            <div className="text-center mb-10">
                <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Your Bag</p>
                <h1 className="font-serif text-5xl md:text-6xl mt-2" style={{ color: 'var(--brand-burgundy)' }}>Review & Request</h1>
                <hr className="divider-burgundy mt-5 mx-auto" />
            </div>

            {items.length === 0 ? (
                <div className="py-16 text-center luxury-card" data-testid="cart-empty-state">
                    <ShoppingBag className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(90,36,50,0.35)' }} />
                    <p className="font-serif text-3xl" style={{ color: 'var(--brand-burgundy)' }}>Your bag is quiet.</p>
                    <Link to="/shop" className="mt-6 inline-block btn-burgundy px-6 py-3 rounded-xl text-xs tracking-[0.20em] uppercase">Shop All</Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Items */}
                    <div className="lg:col-span-7">
                        <ul className="space-y-4" data-testid="cart-items-list">
                            {items.map((it) => {
                                const key = lineKey(it);
                                return (
                                    <li key={key} className="luxury-card p-4 flex gap-4" data-testid={`cart-line-item-${it.product_slug}`}>
                                        <div className="w-24 h-28 rounded-lg overflow-hidden bg-[var(--brand-cream)] flex-shrink-0">
                                            {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <Link to={`/product/${it.product_slug}`} className="font-serif text-xl hover:underline" style={{ color: 'var(--brand-burgundy)' }}>{it.name}</Link>
                                                    <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                                                        {it.size && <>Size: {it.size}</>}{it.size && it.color && <span> · </span>}{it.color && <>Color: {it.color}</>}
                                                    </p>
                                                    {it.special_request && <p className="text-xs mt-1 italic" style={{ color: 'var(--brand-burgundy)' }}>Note: {it.special_request}</p>}
                                                </div>
                                                <button onClick={() => removeItem(key)} className="p-1 hover:opacity-70" aria-label="Remove">
                                                    <Trash2 className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                                                </button>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between">
                                                <div className="flex items-center border border-[rgba(90,36,50,0.18)] rounded-lg bg-white">
                                                    <button onClick={() => updateQty(key, it.quantity - 1)} className="p-1.5" aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
                                                    <span className="px-3 text-sm">{it.quantity}</span>
                                                    <button onClick={() => updateQty(key, it.quantity + 1)} className="p-1.5" aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
                                                </div>
                                                <p className="font-serif text-lg">{format(it.price * it.quantity)}</p>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Checkout */}
                    <div className="lg:col-span-5">
                        <div className="luxury-card p-6 lg:p-8 sticky top-28">
                            <h2 className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Submit a Purchase Request</h2>
                            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Our atelier will confirm availability, total and payment privately with you.</p>
                            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="checkout-form">
                                <div>
                                    <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Full Name *</label>
                                    <input data-testid="checkout-name" required className="input-luxury" value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Email *</label>
                                        <input data-testid="checkout-email" required type="email" className="input-luxury" value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Phone *</label>
                                        <input data-testid="checkout-phone" required className="input-luxury" value={form.customer_phone} onChange={(e) => update('customer_phone', e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Address *</label>
                                    <input data-testid="checkout-address" required className="input-luxury" value={form.address} onChange={(e) => update('address', e.target.value)} />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>City *</label>
                                        <input data-testid="checkout-city" required className="input-luxury" value={form.city} onChange={(e) => update('city', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Country *</label>
                                        <input data-testid="checkout-country" required className="input-luxury" value={form.country} onChange={(e) => update('country', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Postal Code</label>
                                        <input data-testid="checkout-postal" className="input-luxury" value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Notes</label>
                                    <textarea data-testid="checkout-notes" rows={3} className="input-luxury resize-none" placeholder="Anything our atelier should know?" value={form.notes} onChange={(e) => update('notes', e.target.value)} />
                                </div>

                                <div className="pt-4 border-t border-[rgba(90,36,50,0.10)] flex items-center justify-between">
                                    <span className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Subtotal</span>
                                    <span className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }} data-testid="checkout-subtotal">{format(subtotal)}</span>
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="checkout-submit-button"
                                    className="btn-burgundy w-full px-6 py-3.5 rounded-xl text-xs tracking-[0.24em] uppercase font-medium disabled:opacity-60"
                                >
                                    {submitting ? 'Submitting...' : 'Request Purchase'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
